import { FC, useMemo } from "react";
import {
  AddressId,
  Nullable,
  useAdminServiceAlertsControllerDeleteById,
  useAdminServiceAlertsControllerSendServiceAlert,
  useAdminServiceAlertsControllerUpdate,
} from "service";
import GenericTable, {
  RenderGenericRowFunction,
  SortOptions,
} from "../components/generic-table/generic-table";
import { useDialogStore } from "../../../state/dialog-store";
import { toast } from "react-toastify";
import { ServiceAlertDto } from "service/api/estabrookSchemas";
import InteractableCell from "../components/generic-table/interactable-cell";
import { notificationsServiceAlertStatusEnumLabelMap } from "service/types/enum/notifications-enum";
import { Box, Chip, Typography, useTheme } from "@mui/material";
import { formatDateTime } from "../../../utils/string-helpers";
import InlineEditField from "../components/generic-table/inline-edit-field";
import { useNavigate } from "react-router-dom";
import { generateRoute } from "ui";
import { AdRoutes } from "../../../constants/ad-routes";

const sanitizedHeader = [
  {
    label: "Message",
    value: "message",
  },
  {
    label: "Address & Recipient",
    value: "addressRecipient",
    sortable: true,
    sortableValue: "customerName",
    size: "16%",
  },
  {
    label: "Driver",
    value: "driver",
    sortable: true,
    sortableValue: "driverName",
    size: "14%",
  },
  {
    label: "Status",
    value: "status",
    size: "8%",
    sortable: true,
    sortableValue: "status",
  },
  {
    label: "Date Sent",
    value: "dateSent",
    sortable: true,
    sortableValue: "sentAt",
    size: "18%",
  },
];

export interface NotificationsManagementServiceAlertsTableProps {
  data?: ServiceAlertDto[];
  sortOptions?: Nullable<SortOptions>;
  sortOptionsChange?: (value: Nullable<SortOptions>) => void;
  isLoading?: boolean;
  refetch: () => void;
}

const NotificationsManagementServiceAlertsTable: FC<
  NotificationsManagementServiceAlertsTableProps
> = (props) => {
  const navigate = useNavigate();
  const {
    data = [],
    sortOptions,
    sortOptionsChange,
    isLoading,
    refetch,
  } = props;
  const { setDialog } = useDialogStore();
  const { palette } = useTheme();

  const { mutate } = useAdminServiceAlertsControllerDeleteById({
    onSuccess: () => {
      toast.success("Service Alert Deleted");
      refetch();
    },
  });

  const { mutate: updateAlert } = useAdminServiceAlertsControllerUpdate({
    onSuccess: () => {
      toast.success("Service Alert Updated");
      refetch();
    },
  });

  const { mutate: sendAlert } = useAdminServiceAlertsControllerSendServiceAlert(
    {
      onSuccess: () => {
        toast.success("Service alert Sent");
        refetch();
      },
    },
  );

  const onServiceAlertDetail = (id: AddressId) => {
    navigate(
      generateRoute(AdRoutes.notificationServiceAlertDetails, {
        notificationId: id,
      }),
    );
  };

  const onDeletePress = async (serviceAlertId: string) => {
    setDialog({
      title: "Delete alert",
      description: `Are you sure you want to delete this alert?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      withCancelAction: true,
      onConfirm: async (onClose) => {
        mutate({ pathParams: { serviceAlertId } });
        onClose();
      },
    });
  };

  const onSendPress = async (serviceAlertId: string) => {
    setDialog({
      title: "Send alert now",
      description: `Are you sure you want to send service alert?`,
      confirmText: "Send now",
      cancelText: "Cancel",
      onConfirm: async (onClose) => {
        sendAlert({ pathParams: { serviceAlertId } });
        onClose();
      },
    });
  };

  const renderRowLabelMap: Record<
    string,
    RenderGenericRowFunction<ServiceAlertDto>
  > = useMemo(
    () => ({
      message: ({ headerValue, data: { message, id } }) => ({
        label: (
          <InlineEditField
            initialValue={message}
            saveChanges={(value) => {
              updateAlert({
                pathParams: { serviceAlertId: id },
                body: { message: value },
              });
            }}
          />
        ),
        value: headerValue,
      }),
      addressRecipient: ({ headerValue, data: { customer, location } }) => ({
        label: (
          <Box style={{ flexDirection: "column" }}>
            <Typography variant="body2">{location.address}</Typography>
            <Typography variant="caption">{customer.name}</Typography>
          </Box>
        ),
        value: headerValue,
      }),
      driver: ({ headerValue, data: { driver } }) => ({
        label: driver.name,
        value: headerValue,
      }),
      status: ({ headerValue, data: { status } }) => ({
        label: (
          <Chip
            label={notificationsServiceAlertStatusEnumLabelMap[status]}
            sx={{
              backgroundColor:
                status === "sent" ? palette.grey[500] : palette.grey[400],
            }}
            data-testid="notification-status"
          />
        ),
        value: headerValue,
      }),
      dateSent: ({ headerValue, data }) => {
        const { id, sentAt } = data;
        return {
          label: (
            <InteractableCell
              label={
                sentAt !== null ? (
                  <Typography variant="bodyMedium">
                    {formatDateTime(sentAt)}
                  </Typography>
                ) : (
                  "-"
                )
              }
              onEdit={() => onServiceAlertDetail(id)}
              onDelete={() => onDeletePress(id)}
              onSend={() => onSendPress(id)}
            />
          ),
          value: headerValue,
        };
      },
    }),
    [],
  );

  return (
    <GenericTable
      sx={{ mt: "20px" }}
      headers={sanitizedHeader}
      renderRowLabel={renderRowLabelMap}
      data={data}
      sortOptions={sortOptions}
      onSortChange={sortOptionsChange}
      isLoading={isLoading}
    />
  );
};

export default NotificationsManagementServiceAlertsTable;
